import React, { useEffect, useState, useCallback } from "react";
import { CCard, CCardBody, CCardHeader, CButton, CSpinner, CBadge } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilBolt } from "@coreui/icons";
import { getMenuInsight } from "../../api/hermesApi";
import ErrorAlert from "../UI/Status/ErrorAlert";

function Section({ title, items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-3">
      <div className={`small fw-semibold mb-1 text-${color}`}>{title}</div>
      <ul className="mb-0 ps-3">
        {items.map((it, i) => (
          <li key={i}>
            <strong>{it.name}</strong> — {it.reason}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Same button-triggered, never-auto-fetch UX as the dashboard HermesPanel — GigaChat calls cost tokens. */
export default function MenuInsightPanel({ serverId, dateFrom, dateTo, groupByRestaurant = false }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requested, setRequested] = useState(false);

  const fetchInsight = useCallback(
    (force) => {
      if (!serverId || !dateFrom || !dateTo) return;
      setRequested(true);
      setLoading(true);
      setError(null);
      getMenuInsight({ id: serverId, dateFrom, dateTo, groupByRestaurant, force })
        .then(setInsight)
        .catch(setError)
        .finally(() => setLoading(false));
    },
    [serverId, dateFrom, dateTo, groupByRestaurant],
  );

  useEffect(() => {
    setInsight(null);
    setError(null);
    setRequested(false);
  }, [serverId, dateFrom, dateTo, groupByRestaurant]);

  if (!serverId) return null;
  const content = insight?.content;

  return (
    <CCard className="mt-4">
      <CCardHeader className="d-flex align-items-center justify-content-between py-2">
        <div className="d-flex align-items-center gap-2">
          <CIcon icon={cilBolt} className="text-warning" />
          <span className="fw-semibold">AI-анализ меню</span>
          {insight?.cached && (
            <CBadge color="secondary" title={insight.generated_at}>
              кэш
            </CBadge>
          )}
        </div>
        <CButton
          size="sm"
          color="primary"
          variant="outline"
          onClick={() => fetchInsight(requested)}
          disabled={loading || !dateFrom || !dateTo}
        >
          {loading ? <CSpinner size="sm" /> : requested ? "Обновить анализ" : "Запустить анализ"}
        </CButton>
      </CCardHeader>
      <CCardBody className="pt-3">
        <ErrorAlert error={error} title="Не удалось получить анализ" />

        {insight?.date_clamped && (
          <div className="small text-body-secondary mb-3">
            Сегодняшний день ещё не завершён и исключён из анализа — период скорректирован до{" "}
            {insight.date_to}.
          </div>
        )}

        {!requested && !loading && (
          <div className="text-body-secondary py-2">
            Нажмите «Запустить анализ», чтобы получить рекомендации по продвижению, цене и выводу блюд
            из меню за выбранный период.
          </div>
        )}

        {loading && !content && (
          <div className="d-flex align-items-center gap-2 text-body-secondary py-2">
            <CSpinner size="sm" />
            Hermes анализирует меню…
          </div>
        )}

        {content && (
          <>
            <p className="mb-3">{content.summary}</p>
            <Section title="Продвигать" items={content.promote} color="success" />
            <Section title="Пересмотреть цену" items={content.reprice} color="warning" />
            <Section title="Кандидаты на вывод из меню" items={content.remove_candidates} color="danger" />
            {!content.promote?.length && !content.reprice?.length && !content.remove_candidates?.length && (
              <div className="text-body-secondary small">Явных рекомендаций по меню не найдено.</div>
            )}
          </>
        )}
      </CCardBody>
    </CCard>
  );
}
